/**
 * accessModel — ONE shape for "a person who has access to this record".
 *
 * Five endpoints answer "who is on this" and no two of them agree on casing.
 * A task collaborator is `{ Name, Email, ProfilePicture, uuid }`; a project's
 * is `{ name, email, profile_picture, user_id }`; a matter's is
 * `{ FullName, avatar_url, collaborator_id }`; a contact's is
 * `{ first_name, last_name, access_level, is_lead }`; a data source's is
 * `{ name, avatarUrl, user_uuid }`. Every pane that wanted to show a list of
 * people therefore carried its own `collab.name || collab.FullName ||
 * collab.Name || collab.email` chain, and each of them was missing a case the
 * others had — which is why a matter collaborator with only `FullName` drew a
 * blank row in one place and a name in another.
 *
 * The chains live here now, once, and every access surface reads
 * `AccessPerson`. Adding a sixth backend shape means adding a key to a
 * fallback list in this file and a case to `accessModel.test.ts` — not
 * touching seven components.
 *
 * DELIBERATELY NOT A COMPONENT AND NOT A HOOK: it is a pure function, so the
 * shapes can be pinned by tests against the payloads the endpoints actually
 * return.
 */

/** What every access surface renders. Nothing optional, nothing casing-dependent. */
export interface AccessPerson {
  /** Stable row key, and what the manage panel removes by. Never empty. */
  id: string;
  name: string;
  /** '' when the backend has none — the row renders without the second line. */
  email: string;
  initials: string;
  avatar: string | null;
  /** The normalised level. Legacy `admin` rows keep reading as `admin`. */
  access: AccessLevel;
  /** The raw role string, kept for callers that still branch on it. */
  role: string;
  /** Creator/admin — the row that pins to the top and cannot be removed. */
  isOwner: boolean;
  /** Contacts only: the responsible lawyer. */
  isLead: boolean;
}

export type AccessLevel = 'view' | 'edit' | 'admin';

const firstString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return '';
};

/**
 * Initials from a display name.
 *
 * First + last, so "Ada Byron Lovelace" reads AL rather than AB — the same
 * rule `TaskDetail.normalizeCollaborator` used, kept because it is what the
 * avatars on screen already show.
 */
export const initialsFrom = (name: string, email = ''): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  if (parts.length === 1) return parts[0][0].toUpperCase();
  if (email) return email[0].toUpperCase();
  return '?';
};

/**
 * Normalise an access level.
 *
 * Contacts speak `full_access` / `edit_access` / `view_access`; everything
 * else speaks `admin` / `edit` / `view`. `admin` is NOT assignable any more
 * (see `ResourceCollaboratorsSidePanel`) but legacy rows carry it, and
 * silently downgrading them to `view` would misreport what someone can do.
 */
export const normaliseAccessLevel = (raw: unknown, role = ''): AccessLevel => {
  const value = String(raw ?? '').toLowerCase();
  /*
    `owner` and `creator` are LEVELS too, not only roles.

    They were read in the role branch below and nowhere else, so a row that
    said `access: 'owner'` — which is how the playbook endpoint marks the
    person who made it — matched none of these three and fell through to the
    role check with an empty role, landing on `view`. `isOwner` then came out
    false, the owner sorted in with everyone else, and anything asking "who
    created this" got nothing. Whoever holds `owner` holds at least what an
    admin holds, so it reads as `admin`.
  */
  if (value === 'admin' || value === 'full_access'
      || value === 'owner' || value === 'creator') return 'admin';
  if (value === 'edit' || value === 'edit_access' || value === 'write') return 'edit';
  if (value === 'view' || value === 'view_access' || value === 'read') return 'view';

  // No access field at all: the role is the only signal. A `creator` is an
  // admin of their own record; an `editor` can write.
  const r = role.toLowerCase();
  if (r === 'admin' || r === 'creator' || r === 'owner') return 'admin';
  if (r === 'editor' || r === 'edit') return 'edit';
  return 'view';
};

/**
 * One raw collaborator → one `AccessPerson`.
 *
 * The key order in each fallback chain is the order the shapes were added, so
 * a backend that starts sending both a legacy and a current key keeps
 * rendering what it rendered yesterday.
 */
export const normalise = (raw: any): AccessPerson => {
  const source = raw || {};

  const email = firstString(source.email, source.Email, source.user_email);

  const composed = firstString(
    [source.first_name, source.last_name].filter(Boolean).join(' '),
    [source.FirstName, source.LastName].filter(Boolean).join(' ')
  );

  const name =
    firstString(
      source.name,
      source.Name,
      source.FullName,
      source.full_name,
      source.display_name,
      composed
    ) || email;

  const role = firstString(source.role, source.Role, source.access_role);

  const access = normaliseAccessLevel(
    firstString(source.access, source.access_level, source.Access, source.permission),
    role
  );

  const id = firstString(
    source.uuid,
    source.collaborator_id,
    source.collaborator_uuid,
    source.user_uuid,
    source.user_id,
    source.UserID,
    source.UID,
    source.id,
    email
  );

  const initials =
    firstString(source.initials, source.Initials) ||
    firstString(
      [source.first_name?.[0], source.last_name?.[0]].filter(Boolean).join('')
    ).toUpperCase() ||
    initialsFrom(name, email);

  const avatar =
    firstString(
      /*
        `avatar` FIRST, so this function accepts its own output.

        An `AccessPerson` carries `avatar`; every backend shape carries some
        spelling of `profile_picture`. A caller that normalised a row and then
        handed the result to something that normalises again — easily done,
        since both take "a person" — lost the picture on the second pass and
        rendered initials next to a stack that showed the photo. Idempotent
        now: normalising twice is the same as normalising once.
      */
      source.avatar,
      source.avatar_url,
      source.avatarUrl,
      source.avatarURL,
      source.profile_picture,
      source.ProfilePicture,
      source.profilePicture,
      // Comment/activity rows name the picture after the relation rather than
      // the person ("the avatar OF the user"), so they need their own keys.
      source.user_avatar_url,
      source.user_profile_picture,
      source.requester_avatar_url,
      source.user?.profile_picture,
      source.user?.ProfilePicture,
      source.User?.ProfilePicture,
      source.LawFirmProfile?.ProfilePicture
    ) || null;

  return {
    id,
    name,
    email,
    initials,
    avatar,
    access,
    role,
    // The rule every pane had inlined: a creator, or a legacy admin grant.
    isOwner: role.toLowerCase() === 'creator' || role.toLowerCase() === 'owner' || access === 'admin',
    isLead: source.is_lead === true || source.isLead === true,
  };
};

/** The list form. Null/undefined in, empty array out — panes render an empty state. */
export const normaliseAll = (raw: any[] | null | undefined): AccessPerson[] =>
  (raw || []).map(normalise);

/**
 * WHO MADE THIS, as far as an access list will say — the RAW row, or undefined.
 *
 * Some records carry no `created_by` at all (playbooks, today; a backend
 * follow-up). What they carry is an access list in which the creator's entry
 * holds an owner/admin level, so the creator is the entry the access model
 * calls an owner.
 *
 * Undefined when no entry claims that level, and that restraint is the whole
 * point: "someone has access" is not the same claim as "someone made this",
 * and falling back to `rows[0]` attributes a shared record to whoever the
 * backend happened to list first. The picker modals were doing exactly that
 * while the templates list refused to — the same record named two different
 * creators depending on which screen you opened it from.
 *
 * Returns the row AS IT ARRIVED so whatever renders it normalises exactly
 * once; `normalise` is idempotent, but the caller should not have to know.
 */
export const ownerOf = <T,>(rows: T[] | null | undefined): T | undefined => {
  const list = rows || [];
  const index = normaliseAll(list as any[]).findIndex((p) => p.isOwner);
  return index === -1 ? undefined : list[index];
};

/**
 * The owner first, then everyone else in the order the backend sent them.
 *
 * Stable: two people with the same `isOwner` keep their relative positions, so
 * a list does not reshuffle between fetches.
 */
export const ownerFirst = (people: AccessPerson[]): AccessPerson[] => [
  ...people.filter((p) => p.isOwner || p.isLead),
  ...people.filter((p) => !p.isOwner && !p.isLead),
];
